// src/app/service/recaptcha/recaptcha.service.ts
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

declare global {
  interface Window { grecaptcha: any; }
}

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  private siteKey = environment.recaptchaSiteKey;
  private enabled = environment.recaptchaEnabled;

  isEnabled(): boolean {
    return this.enabled;
  }

  getSiteKey(): string {
    return this.siteKey;
  }

  /**
   * Ejecuta reCAPTCHA v3 y obtiene un token
   * Asegura que el script está cargado; si no lo está lo carga dinámicamente.
   */
  execute(action: string = 'submit'): Promise<string> {
    if (!this.enabled) {
      return Promise.reject(new Error('reCAPTCHA deshabilitado'));
    }

    if (!this.siteKey) {
      return Promise.reject(new Error('SiteKey de reCAPTCHA no configurada'));
    }

    return this.ensureGreCaptchaLoaded().then(() => {
      return new Promise<string>((resolve, reject) => {
        try {
          window.grecaptcha.ready(() => {
            window.grecaptcha.execute(this.siteKey, { action })
              .then((token: string) => {
                if (!token) {
                  reject(new Error('Token vacío'));
                  return;
                }
                resolve(token);
              })
              .catch((err: any) => reject(err));
          });
        } catch (err) {
          reject(err);
        }
      });
    });
  }

  /**
   * Si grecaptcha no está definido, carga el script con el siteKey y espera a que cargue.
   */
  private ensureGreCaptchaLoaded(): Promise<void> {
    if (typeof window.grecaptcha !== 'undefined') {
      return Promise.resolve();
    }

    // Si ya se está cargando, esperar al evento onload del script existente
    const existing = document.querySelector(`script[data-recaptcha="v3"]`);
    if (existing) {
      return new Promise((resolve, reject) => {
        (existing as HTMLScriptElement).addEventListener('load', () => resolve());
        (existing as HTMLScriptElement).addEventListener('error', () => reject(new Error('Error cargando grecaptcha')));
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-recaptcha', 'v3');
      script.onload = () => {
        // espera un tick para que window.grecaptcha se inicialice
        setTimeout(() => {
          if (typeof window.grecaptcha === 'undefined') {
            reject(new Error('grecaptcha no disponible después de cargar script'));
          } else {
            resolve();
          }
        }, 50);
      };
      script.onerror = () => reject(new Error('Error cargando script de reCAPTCHA'));
      document.head.appendChild(script);
    });
  }
}
